/**
 * Cadence.tsx — /#/cadence
 *
 * W37-A: BDR Cadence Queue — the page the BDR manual Chapter 5 describes.
 *
 * Data pattern: "Both — cadence_enrollments first, prospect_snapshot fills gaps"
 *   PRIMARY:   cadence_enrollments joined to prospects — active steps due today
 *   SECONDARY: prospect_snapshot — active BDR prospects with no open enrollment,
 *              auto-generates a next-step prompt from current_step / status
 *
 * Canon rules:
 *   - No exclamation points. No emojis.
 *   - M-PACT hyphenated.
 *   - "Checkpoint" not "Gate"
 *   - 5-color palette: gray / red / orange / yellow / green
 *   - CP em-dash format: "CP{N} — {Name}"
 *   - No HARD-KILL / BANT / MEDDIC vocabulary
 */

import { useEffect, useState, useMemo } from "react";
import { Link } from "wouter";
import { ListTodo } from "lucide-react";
import { PageShell } from "@/components/Shell";
import { getSession, supabase } from "@/lib/session";
import { getImpersonationTarget } from "@/lib/impersonation";
import { useToast } from "@/hooks/use-toast";

// ─── CP stage prompts (canonical CP names) ────────────────────────────────────

const CP_PROMPTS: Record<number, string> = {
  1: "CP1 — Project: First contact required — make outbound call",
  2: "CP2 — Need / Pain / Demonstration: Confirm pain and align demo",
  3: "CP3 — Budget: Confirm budget range",
  4: "CP4 — Timeline: Confirm decision and implementation window",
  5: "CP5 — Authority — Project / Initiative: Confirm project authority",
  6: "CP6 — Technical and Financial Sign-off: Confirm evaluation gates",
  7: "CP7 — Org Structure and Influence Map: Map stakeholder landscape",
  8: "CP8 — Competitive Position: Assess competitive threats",
  9: "CP9 — Compliance — IT Internal and Industry-Specific: Check compliance path",
  10: "CP10 — Legal — Contracts, SLAs, Terms: Confirm contract path",
};

function cpPrompt(step: number | null): string {
  const s = step ?? 1;
  return CP_PROMPTS[Math.min(Math.max(s, 1), 10)] ?? CP_PROMPTS[1];
}

// ─── Step type → label ────────────────────────────────────────────────────────

const STEP_TYPE_LABELS: Record<string, string> = {
  email_opening:  "Email",
  email_followup: "Email",
  linkedin_dm:    "LinkedIn",
  phone_no_vm:    "Call",
  phone_with_vm:  "Call",
  decision_gate:  "Decision",
  call:           "Call",
  email:          "Email",
  linkedin:       "LinkedIn",
  research:       "Research",
};

function stepTypeLabel(kind: string | null): string {
  if (!kind) return "Call";
  return STEP_TYPE_LABELS[kind] ?? kind;
}

// Step number → default action kind mapping (Mi6 6-step cadence)
const STEP_KIND: Record<number, string> = {
  1: "email_opening",
  2: "phone_no_vm",
  3: "linkedin_dm",
  4: "phone_no_vm",
  5: "email_followup",
  6: "phone_with_vm",
  7: "decision_gate",
};

// ─── Filter pill type ─────────────────────────────────────────────────────────

type FilterKind = "All" | "Calls" | "Emails" | "LinkedIn" | "Other";

// ─── Queue item (merged from both sources) ────────────────────────────────────

interface QueueItem {
  key: string;
  source: "cadence_enrollments" | "prospect_snapshot";

  // Identifiers
  enrollmentId?: string;
  prospectId?: number | null;
  prospectSupabaseId?: number | null;

  // Display
  contactName: string;
  accountName: string;
  stepType: string;       // "Call" | "Email" | "LinkedIn" | "Research"
  stepTypeRaw: string;    // raw action_kind value
  stepNumber: number;
  dueDate: string | null; // ISO date string
  actionDescription: string;
  hookLabel?: string;     // e.g. "Hook: event"

  // State
  completed: boolean;
  skipped: boolean;
}

// ─── Filter helper ────────────────────────────────────────────────────────────

function matchesFilter(item: QueueItem, filter: FilterKind): boolean {
  if (filter === "All") return true;
  const t = item.stepType;
  if (filter === "Calls")    return t === "Call";
  if (filter === "Emails")   return t === "Email";
  if (filter === "LinkedIn") return t === "LinkedIn";
  if (filter === "Other")    return t === "Research" || (t !== "Call" && t !== "Email" && t !== "LinkedIn");
  return true;
}

// ─── Source badge ─────────────────────────────────────────────────────────────

function SourceBadge({ source }: { source: QueueItem["source"] }) {
  const label = source === "cadence_enrollments" ? "via cadence" : "via prospect";
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        background: "#F4F4F5",
        color: "#6B6B70",
        border: "1px solid #E5E5E7",
        borderRadius: 4,
        padding: "1px 5px",
      }}
    >
      {label}
    </span>
  );
}

// ─── Step type badge ──────────────────────────────────────────────────────────

const STEP_TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  Call:     { bg: "#FEF3C7", text: "#92400E" },
  Email:    { bg: "#DBEAFE", text: "#1E40AF" },
  LinkedIn: { bg: "#E0E7FF", text: "#3730A3" },
  Research: { bg: "#F3F4F6", text: "#374151" },
};

function StepTypeBadge({ label }: { label: string }) {
  const colors = STEP_TYPE_COLORS[label] ?? STEP_TYPE_COLORS.Research;
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.10em",
        textTransform: "uppercase",
        background: colors.bg,
        color: colors.text,
        borderRadius: 4,
        padding: "1px 6px",
      }}
    >
      {label}
    </span>
  );
}

// ─── Queue item card ──────────────────────────────────────────────────────────

function QueueCard({
  item,
  onComplete,
  onSkip,
  onReschedule,
}: {
  item: QueueItem;
  onComplete: (item: QueueItem) => void;
  onSkip: (item: QueueItem) => void;
  onReschedule: (item: QueueItem) => void;
}) {
  const isOverdue = !!item.dueDate && item.dueDate < new Date().toISOString().slice(0, 10);
  const isDone = item.completed || item.skipped;

  return (
    <div
      style={{
        background: "#FFFFFF",
        border: `1px solid ${isOverdue && !isDone ? "#DC2626" : "#E5E5E7"}`,
        borderLeft: `3px solid ${isDone ? "#D1D5DB" : isOverdue ? "#DC2626" : "#0B0B0F"}`,
        borderRadius: 8,
        padding: "12px 14px",
        opacity: isDone ? 0.6 : 1,
      }}
    >
      {/* Row 1 */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
        <span
          style={{
            fontWeight: 700,
            fontSize: 14,
            color: "#0B0B0F",
            textDecoration: isDone ? "line-through" : "none",
          }}
        >
          {item.contactName}
        </span>
        {item.accountName && (
          <>
            <span style={{ color: "#9CA3AF", fontSize: 12 }}>·</span>
            <span style={{ fontSize: 12, color: "#374151" }}>{item.accountName}</span>
          </>
        )}
        <SourceBadge source={item.source} />
        {isOverdue && !isDone && (
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              background: "#DC2626",
              color: "#FFFFFF",
              borderRadius: 4,
              padding: "1px 5px",
            }}
          >
            Overdue
          </span>
        )}
      </div>

      {/* Row 2 */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
        <StepTypeBadge label={item.stepType} />
        <span style={{ fontSize: 11, color: "#6B6B70" }}>
          Step {item.stepNumber}
        </span>
        {item.dueDate && (
          <span style={{ fontSize: 11, color: isOverdue && !isDone ? "#DC2626" : "#6B6B70" }}>
            Due: {new Date(item.dueDate + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </span>
        )}
        {item.hookLabel && (
          <span style={{ fontSize: 11, color: "#059669" }}>
            {item.hookLabel}
          </span>
        )}
      </div>

      {/* Row 3 + action buttons */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <p style={{ fontSize: 12, color: "#374151", lineHeight: 1.4, flex: 1, margin: 0 }}>
          {item.actionDescription}
        </p>
        {!isDone && (
          <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap" }}>
            <button
              onClick={() => onComplete(item)}
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.10em",
                textTransform: "uppercase",
                padding: "5px 10px",
                borderRadius: 6,
                border: "none",
                background: "#16A34A",
                color: "#FFFFFF",
                cursor: "pointer",
              }}
            >
              Log complete
            </button>
            <button
              onClick={() => onSkip(item)}
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.10em",
                textTransform: "uppercase",
                padding: "5px 10px",
                borderRadius: 6,
                border: "1px solid #E5E5E7",
                background: "#F9FAFB",
                color: "#6B6B70",
                cursor: "pointer",
              }}
            >
              Skip
            </button>
            {item.dueDate && (
              <button
                onClick={() => onReschedule(item)}
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.10em",
                  textTransform: "uppercase",
                  padding: "5px 10px",
                  borderRadius: 6,
                  border: "1px solid #CA8A04",
                  background: "#FFFBEB",
                  color: "#92400E",
                  cursor: "pointer",
                }}
              >
                Reschedule
              </button>
            )}
            {item.prospectId && (
              <Link
                href={`/account/${item.prospectId}`}
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.10em",
                  textTransform: "uppercase",
                  padding: "5px 10px",
                  borderRadius: 6,
                  border: "1px solid #E5E5E7",
                  background: "#FFFFFF",
                  color: "#0B0B0F",
                  textDecoration: "none",
                  cursor: "pointer",
                }}
              >
                Open prospect
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Cadence() {
  const session = getSession();
  const target = getImpersonationTarget();
  const userId = target?.id ?? session?.id ?? null;
  const userName = target?.name ?? session?.full_name ?? "BDR";
  const { toast } = useToast();

  const [items, setItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterKind>("All");
  const [sortByPriority, setSortByPriority] = useState(false);
  const [hideCompleted, setHideCompleted] = useState(false);
  const [tick, setTick] = useState(0);

  // ── Data fetch ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchQueue() {
      setLoading(true);
      try {
        const db = supabase();
        const today = new Date().toISOString().slice(0, 10);
        const merged: QueueItem[] = [];

        // ── PRIMARY: cadence_enrollments with active/pending steps ────────────
        // Join to prospects for contact and company info
        const { data: enrollments, error: enrollErr } = await db
          .from("cadence_enrollments")
          .select(`
            id,
            prospect_id,
            current_step,
            status,
            next_action_at,
            marketing_hook_kind,
            notes,
            prospects (
              id,
              full_name,
              company,
              title
            )
          `)
          .eq("owner_id", userId)
          .is("completed_at", null)
          .in("status", ["active", "pending", "lead"])
          .order("next_action_at", { ascending: true, nullsFirst: false });

        if (enrollErr) {
          console.warn("[Cadence] enrollment fetch error:", enrollErr.message);
        }

        const enrolledProspectIds = new Set<number>();

        if (enrollments) {
          for (const enroll of enrollments) {
            const step = enroll.current_step ?? 1;
            const kind = STEP_KIND[step] ?? "phone_no_vm";
            const prospect = enroll.prospects as any;
            const contactName = prospect?.full_name ?? "Unknown contact";
            const accountName = prospect?.company ?? "";

            enrolledProspectIds.add(enroll.prospect_id);

            merged.push({
              key: `enroll-${enroll.id}`,
              source: "cadence_enrollments",
              enrollmentId: enroll.id,
              prospectId: enroll.prospect_id,
              contactName,
              accountName,
              stepType: stepTypeLabel(kind),
              stepTypeRaw: kind,
              stepNumber: step,
              dueDate: enroll.next_action_at ?? null,
              actionDescription: cpPrompt(step),
              hookLabel: enroll.marketing_hook_kind
                ? `Hook: ${enroll.marketing_hook_kind.replace(/_/g, " ")}`
                : undefined,
              completed: false,
              skipped: false,
            });
          }
        }

        // ── SECONDARY: prospect_snapshot gap-fill ─────────────────────────────
        // Active BDR prospects with no open enrollment entry
        const { data: snapshots, error: snapErr } = await db
          .from("prospect_snapshot")
          .select(`
            id,
            prospect_id,
            owner_user_id,
            status,
            last_action_at,
            taken_at,
            prospects (
              id,
              full_name,
              company,
              current_step,
              status
            )
          `)
          .eq("owner_user_id", userId)
          .eq("status", "active")
          .order("taken_at", { ascending: true });

        if (snapErr) {
          console.warn("[Cadence] snapshot fetch error:", snapErr.message);
        }

        if (snapshots) {
          for (const snap of snapshots) {
            // Skip if already has an active enrollment
            if (enrolledProspectIds.has(snap.prospect_id)) continue;

            const prospect = snap.prospects as any;
            const step = prospect?.current_step ?? 1;
            const kind = STEP_KIND[step] ?? "phone_no_vm";

            merged.push({
              key: `snap-${snap.id}`,
              source: "prospect_snapshot",
              prospectId: snap.prospect_id,
              contactName: prospect?.full_name ?? "Unknown contact",
              accountName: prospect?.company ?? "",
              stepType: stepTypeLabel(kind),
              stepTypeRaw: kind,
              stepNumber: step,
              dueDate: null, // snapshot has no due date — it's overdue by definition
              actionDescription: cpPrompt(step),
              completed: false,
              skipped: false,
            });
          }
        }

        if (!cancelled) {
          setItems(merged);
          setLoading(false);
        }
      } catch (err: any) {
        console.error("[Cadence] fetch error:", err?.message);
        if (!cancelled) setLoading(false);
      }
    }

    fetchQueue();
    return () => { cancelled = true; };
  }, [userId, tick]);

  // ── Actions ─────────────────────────────────────────────────────────────────

  async function handleComplete(item: QueueItem) {
    // Optimistic update
    setItems((prev) =>
      prev.map((i) => (i.key === item.key ? { ...i, completed: true } : i))
    );

    try {
      const db = supabase();

      if (item.enrollmentId) {
        // Log completion in cadence_step_completions
        const { error } = await db
          .from("cadence_step_completions")
          .insert({
            enrollment_id: item.enrollmentId,
            step_id: item.stepNumber,
            kind: item.stepTypeRaw,
            outcome: "completed",
            completed_at: new Date().toISOString(),
            user_id: userId,
            tenant_id: session?.tenantId ?? 1,
          });
        if (error) throw error;

        // Advance step on enrollment
        await db
          .from("cadence_enrollments")
          .update({
            current_step: item.stepNumber + 1,
            last_action_at: new Date().toISOString(),
            next_action_at: new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10),
          })
          .eq("id", item.enrollmentId);
      } else {
        // Snapshot-derived — log action on cadence_actions
        await db.from("cadence_actions").insert({
          tenant_id: session?.tenantId ?? 1,
          owner_id: userId,
          prospect_id: item.prospectId,
          step_id: item.stepNumber,
          action_kind: item.stepTypeRaw,
          outcome: "completed",
          executed_at: new Date().toISOString(),
        });
      }

      toast({ title: "Step logged as complete" });
    } catch (err: any) {
      console.error("[Cadence] complete error:", err?.message);
      // Revert optimistic update
      setItems((prev) =>
        prev.map((i) => (i.key === item.key ? { ...i, completed: false } : i))
      );
      toast({
        title: "Could not log step",
        description: err?.message ?? "Check your connection and try again.",
        variant: "destructive",
      });
    }
  }

  async function handleSkip(item: QueueItem) {
    setItems((prev) =>
      prev.map((i) => (i.key === item.key ? { ...i, skipped: true } : i))
    );

    try {
      const db = supabase();

      if (item.enrollmentId) {
        const { error } = await db
          .from("cadence_step_completions")
          .insert({
            enrollment_id: item.enrollmentId,
            step_id: item.stepNumber,
            kind: item.stepTypeRaw,
            outcome: "skipped",
            completed_at: new Date().toISOString(),
            user_id: userId,
            tenant_id: session?.tenantId ?? 1,
          });
        if (error) throw error;
      } else {
        await db.from("cadence_actions").insert({
          tenant_id: session?.tenantId ?? 1,
          owner_id: userId,
          prospect_id: item.prospectId,
          step_id: item.stepNumber,
          action_kind: item.stepTypeRaw,
          outcome: "skipped",
          executed_at: new Date().toISOString(),
        });
      }

      toast({ title: "Step skipped" });
    } catch (err: any) {
      setItems((prev) =>
        prev.map((i) => (i.key === item.key ? { ...i, skipped: false } : i))
      );
      toast({
        title: "Could not skip step",
        description: err?.message ?? "Check your connection and try again.",
        variant: "destructive",
      });
    }
  }

  async function handleReschedule(item: QueueItem) {
    if (!item.enrollmentId) {
      toast({ title: "Rescheduling not available for gap-fill items" });
      return;
    }

    const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

    try {
      const db = supabase();
      const { error } = await db
        .from("cadence_enrollments")
        .update({ next_action_at: tomorrow })
        .eq("id", item.enrollmentId);

      if (error) throw error;

      setItems((prev) =>
        prev.map((i) =>
          i.key === item.key ? { ...i, dueDate: tomorrow } : i
        )
      );
      toast({ title: "Step rescheduled to tomorrow" });
    } catch (err: any) {
      toast({
        title: "Could not reschedule",
        description: err?.message ?? "Check your connection and try again.",
        variant: "destructive",
      });
    }
  }

  // ── Computed display list ────────────────────────────────────────────────────

  const displayItems = useMemo(() => {
    let list = items.filter((i) => matchesFilter(i, filter));
    if (hideCompleted) {
      list = list.filter((i) => !i.completed && !i.skipped);
    }
    if (sortByPriority) {
      // Priority: overdue first, then active, then no due date
      const today = new Date().toISOString().slice(0, 10);
      list = [...list].sort((a, b) => {
        const aOver = a.dueDate && a.dueDate < today ? 0 : a.dueDate ? 1 : 2;
        const bOver = b.dueDate && b.dueDate < today ? 0 : b.dueDate ? 1 : 2;
        return aOver - bOver;
      });
    }
    return list;
  }, [items, filter, hideCompleted, sortByPriority]);

  const openCount = items.filter((i) => !i.completed && !i.skipped).length;
  const totalCount = items.length;

  // ── Render ───────────────────────────────────────────────────────────────────

  const FILTERS: FilterKind[] = ["All", "Calls", "Emails", "LinkedIn", "Other"];

  return (
    <PageShell pageLabel="Cadence Queue">
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <ListTodo size={20} color="#0B0B0F" aria-hidden />
            <h1
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: "#0B0B0F",
                margin: 0,
                letterSpacing: "-0.01em",
              }}
            >
              Today's cadence queue — {userName}
            </h1>
          </div>
          <p style={{ fontSize: 13, color: "#6B6B70", margin: 0 }}>
            {loading
              ? "Loading queue..."
              : `${openCount} open steps for today · ${totalCount} total in queue`}
          </p>
        </div>

        {/* Controls row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          {/* Filter pills */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  fontSize: 12,
                  fontWeight: filter === f ? 700 : 500,
                  padding: "4px 12px",
                  borderRadius: 20,
                  border: filter === f ? "1.5px solid #0B0B0F" : "1px solid #E5E5E7",
                  background: filter === f ? "#0B0B0F" : "#FFFFFF",
                  color: filter === f ? "#FFFFFF" : "#6B6B70",
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                {f}
              </button>
            ))}
          </div>

          {/* Sort + hide completed */}
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button
              onClick={() => setSortByPriority((v) => !v)}
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: "4px 10px",
                borderRadius: 6,
                border: "1px solid #E5E5E7",
                background: sortByPriority ? "#FEF3C7" : "#FFFFFF",
                color: sortByPriority ? "#92400E" : "#6B6B70",
                cursor: "pointer",
              }}
            >
              {sortByPriority ? "Sorted: priority" : "Sort by priority"}
            </button>
          </div>
        </div>

        {/* Queue list */}
        {loading ? (
          <div
            style={{
              background: "#F9FAFB",
              borderRadius: 10,
              border: "1px solid #E5E5E7",
              padding: "40px 24px",
              textAlign: "center",
              color: "#6B6B70",
              fontSize: 14,
            }}
          >
            Loading your cadence queue...
          </div>
        ) : displayItems.length === 0 ? (
          <div
            style={{
              background: "#F9FAFB",
              borderRadius: 10,
              border: "1px solid #E5E5E7",
              padding: "40px 24px",
              textAlign: "center",
            }}
          >
            <p style={{ color: "#6B6B70", fontSize: 14, margin: "0 0 16px" }}>
              {items.length === 0
                ? "No cadence steps for today."
                : "No items match the current filter."}
            </p>
            {items.length === 0 && (
              <a
                href="#/marketing/hooks"
                style={{
                  display: "inline-block",
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.10em",
                  textTransform: "uppercase",
                  padding: "8px 16px",
                  borderRadius: 6,
                  background: "#0B0B0F",
                  color: "#FFFFFF",
                  textDecoration: "none",
                }}
              >
                Pull from Hook Library
              </a>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {displayItems.map((item) => (
              <QueueCard
                key={item.key}
                item={item}
                onComplete={handleComplete}
                onSkip={handleSkip}
                onReschedule={handleReschedule}
              />
            ))}
          </div>
        )}

        {/* Bottom controls */}
        {totalCount > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 20,
              padding: "10px 0",
              borderTop: "1px solid #F3F4F6",
            }}
          >
            <span style={{ fontSize: 12, color: "#6B6B70" }}>
              Showing {displayItems.length} of {totalCount} items
            </span>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12,
                color: "#6B6B70",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={hideCompleted}
                onChange={(e) => setHideCompleted(e.target.checked)}
                style={{ width: 14, height: 14, cursor: "pointer" }}
              />
              Hide completed
            </label>
          </div>
        )}

        {/* Manual reference link */}
        <div style={{ marginTop: 32, paddingTop: 16, borderTop: "1px solid #F3F4F6" }}>
          <a
            href="#/manuals/bdr"
            style={{ fontSize: 12, color: "#6B6B70", textDecoration: "none" }}
          >
            BDR Operating Manual — Chapter 5: The Cadence Queue
          </a>
        </div>
      </div>
    </PageShell>
  );
}
